import * as React from "react";
import { Switch as AntSwitch, Button, Dropdown } from "antd";
import { linksOf, request, resourceLink } from "../../../services/cpln";
import { Activity, MoreVertical } from "react-feather";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Message } from "./message";
import { CommandModal_ConfirmReplicaTermination } from "../commands/commandModal_confirmReplicaTermination";
import { Deployment } from "../../../schema/types/workload/deployment";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { useInitCommandsContext } from "../../../components/detail/command/commandsContext";
import { PersistentVolumeStatus, VolumeSet } from "../../../schema/types/volumeSet/entity";
import { Workload } from "../../../schema/types/workload/entity";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { useAppDispatch } from "../../../store/hooks";
import { setDeploymentViewObject } from "../../../store/workloadSlice";
import { LeftOutlined } from "@ant-design/icons";
import { parseLink } from "../../../utils/linkParser/linkParser";

export interface ProcessedPersistentVolumeStatus extends PersistentVolumeStatus {
  volumeSetName: string;
  volumeSetLink: string;
}
interface ConfirmReplicaTerminationProps {
  location: string;
  replica: string;
}
interface Props {
  workload: Workload;
  deployments: Deployment[];
}

export const StatefulReplica: React.FC<Props> = ({ workload, deployments }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const selfLink = linksOf(workload).self!;

  const basePath = React.useContext(BasePathContext);
  const { pathname } = useLocation();
  const { deployment: deploymentParam, replica: replicaParamString, replicaIndex: replicaIndexString } = useParams<{
    deployment: string;
    replica: string;
    replicaIndex: string;
  }>();
  const replicaParam = decodeURI(replicaParamString || "");
  const replicaIndex = Number(replicaIndexString);

  const { commands, startFetchCommandsInterval, fetchCommandsPage } = useInitCommandsContext(linksOf(workload).self!);
  React.useEffect(() => {
    startFetchCommandsInterval();
  }, []);

  const updatedTimeoutIdRef = React.useRef<any>(null);
  const [terminateReplica, setTerminateReplica] = React.useState<ConfirmReplicaTerminationProps | undefined>(undefined);

  function getDefaultViewVolumes() {
    return String(localStorage.getItem("stateful-workload-view-volumes") || "false") === "true";
  }
  const [viewVolumes, _setViewVolumes] = React.useState<boolean>(getDefaultViewVolumes());

  const [isVolumeSetsReady, setIsVolumeSetsReady] = React.useState<boolean>(false);
  const [volumesets, setVolumesets] = React.useState<VolumeSet[]>([]);

  function setViewVolumes(value: boolean) {
    _setViewVolumes(value);
    localStorage.setItem("stateful-workload-view-volumes", String(value));
  }

  const deployment = deployments.find((deployment) => deployment.name === deploymentParam);
  const replica = deployment?.status?.versions?.find((version) => version.name === replicaParam);

  React.useEffect(() => {
    return () => {
      if (updatedTimeoutIdRef.current) {
        clearTimeout(updatedTimeoutIdRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    // TODO do we update the page when a patch is applied?
    fetchVolumesets();
  }, []);

  if (!replica) {
    return (
      <div>
        Replica is not found, <Link to={pathname.split("/").slice(0, -5).join("/")}>go back</Link>
      </div>
    );
  }

  async function fetchVolumesets() {
    const links: string[] = [];
    for (const container of workload.spec.containers) {
      for (const volume of (container.volumes || []).filter((v) => v.uri.startsWith("cpln://volumeset/"))) {
        const name = volume.uri.split("cpln://volumeset/")[1];
        const { org, gvc } = parseLink(selfLink, { useInputCtx: true });
        const link = resourceLink("volumeset", name, { org: org, gvc: gvc });
        if (!links.includes(link)) {
          links.push(link);
        }
      }
    }
    const _volumesets: VolumeSet[] = [];
    for (const link of links) {
      try {
        const { data: volumeset } = await request({ url: link });
        _volumesets.push(volumeset);
      } catch (e) {
        console.error("failed to fetch and create mobx instance for volumeset " + link);
      }
    }
    setVolumesets(_volumesets);
    setIsVolumeSetsReady(true);
  }

  const hasActiveStopCommand = commands.find(
    (command) =>
      command.type === "stopReplica" &&
      command.spec?.location === deploymentParam &&
      command.lifecycleStage === "running",
  );

  return (
    <>
      {!!terminateReplica ? (
        <CommandModal_ConfirmReplicaTermination
          workload={workload}
          location={terminateReplica.location}
          replica={terminateReplica.replica}
          onClose={() => setTerminateReplica(undefined)}
          onDone={() => {
            setTerminateReplica(undefined);
            fetchCommandsPage(true);
          }}
          visible={!!terminateReplica}
        />
      ) : null}
      <div>
        <div className={"mb-4"}>
          <div className="border-b mb-4">
            <Button
              type={"text"}
              className="button-link flex items-center mb-4"
              onClick={() => navigate(pathname.split("/").slice(0, -5).join("/"))}
              icon={<LeftOutlined />}
              iconPosition="start"
            >
              Back to Deployments
            </Button>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex flex-col">
                <FormLabel>Replica</FormLabel>
                <div className={`font-medium flex items-center gap-1`}>
                  <span>{replica.name}</span>
                  <button
                    type={"button"}
                    className={`focus`}
                    onClick={() => dispatch(setDeploymentViewObject({ workloadLink: selfLink, data: replica }))}
                  >
                    <Activity className={`feather-icon ${replica.ready ? "color-action" : "color-danger"}`} />
                  </button>
                </div>
              </div>
              <div className="flex flex-col">
                <FormLabel>Location</FormLabel>
                <div className={`font-medium`}>{deploymentParam}</div>
              </div>
              <div className="mb-4 flex items-center">
                <div className="mr-2">
                  <FormLabel>View Volumes</FormLabel>
                </div>
                <AntSwitch
                  data-testid="switch-capacity-ai"
                  checked={viewVolumes}
                  onChange={setViewVolumes}
                  size={"small"}
                  disabled={!isVolumeSetsReady}
                />
              </div>
              <div className="flex-grow" />
              {replicaParamString &&
              deploymentParam &&
              deployments.find((deployment) => deployment.name === deploymentParam)?.status?.remote ? (
                <Button
                  type={"default"}
                  onClick={() => setTerminateReplica({ location: deploymentParam, replica: replicaParamString })}
                >
                  Restart Replica
                </Button>
              ) : null}
            </div>
          </div>
          <Message
            messages={[replica.message || "", ...(hasActiveStopCommand ? [`Replica is being restarted`] : [])]}
            // TODO remove logsLink and eventsLink requirement
            logsLink={pathname.split("/deployments")[0] + "/logs?execute=true"}
            eventsLink={pathname.split("/deployments")[0] + "/events"}
          />
          <div className={`flex items-center px-4 py-2 font-semibold table-labels text-sm`}>
            <div className="w-2/12">Container</div>
            <div className="w-3/12">Image</div>
            <div className="w-7/12 flex items-center">
              <div className="flex-1 flex-grow">Status</div>
              <div className="flex-1 flex-grow">Reserved CPU</div>
              <div className="flex-1 flex-grow">Reserved Memory</div>
              <div className="flex-1 flex-grow">Volume Size</div>
            </div>
          </div>
          {Object.values(replica.containers || {}).map((container, containerIndex) => {
            const lastItem = containerIndex === (Object.values(replica.containers || {}) || []).length - 1;

            // storage size calculation
            const containerVolumeSets = volumesets.filter((vs) => {
              for (const c of workload.spec.containers) {
                if (c.name !== container.name) {
                  continue;
                }
                if ((c.volumes || []).some((v) => v.uri.includes(`cpln://volumeset/${vs.name}`))) {
                  return true;
                }
              }
              return false;
            });

            const containerVolumes: ProcessedPersistentVolumeStatus[] = [];

            for (const volumeSet of containerVolumeSets) {
              const volumeSetLink = linksOf(volumeSet).self!;
              for (const location of volumeSet.status?.locations || []) {
                if (location.name !== deploymentParam) {
                  continue;
                }
                for (const volume of location.volumes || []) {
                  if (volume.index !== replicaIndex) {
                    continue;
                  }
                  containerVolumes.push({ ...volume, volumeSetName: volumeSet.name, volumeSetLink: volumeSetLink });
                }
              }
            }

            let containerStorageSize = 0;
            for (const volume of containerVolumes) {
              containerStorageSize += volume.currentSize;
            }

            return (
              <div
                key={container?.name || "" + containerIndex}
                className={clsx("py-2 flex flex-col", { "border-b": !lastItem })}
              >
                <div className="flex items-center px-4">
                  <div className="w-2/12 flex items-center gap-1">
                    <span>{container.name}</span>
                    <button
                      type={"button"}
                      className={`focus`}
                      onClick={() => dispatch(setDeploymentViewObject({ workloadLink: selfLink, data: container }))}
                    >
                      <Activity className={`feather-icon ${container.ready ? "color-action" : "color-danger"}`} />
                    </button>
                  </div>
                  <div className="w-3/12 truncate pr-2">{container.image}</div>
                  <div className="w-7/12 flex items-center">
                    <div className={`flex-1 flex-grow ${container.ready ? "color-action" : "color-danger"}`}>
                      {container.ready ? "Ready" : "Not Ready"}
                    </div>
                    <div className="flex-1 flex-grow">{container.resources?.cpu}m</div>
                    <div className="flex-1 flex-grow">{container.resources?.memory}Mi</div>
                    <div className="flex-1 flex-grow flex items-center">
                      <div>{containerStorageSize}GB</div>
                      <div className="flex-grow" />
                      <Dropdown
                        trigger={["click"]}
                        placement={"bottomRight"}
                        menu={{
                          items: [
                            { key: "logs", label: "Logs" },
                            { key: "tail", label: "Tail" },
                            { key: "connect", label: "Connect" },
                          ],
                          className: "menu",
                          onClick: ({ key }) => {
                            const workloadGvc = parseLink(selfLink, { useInputCtx: true }).gvc;

                            switch (key) {
                              case "logs":
                                const logLink = `${basePath}/logs?query=${encodeURIComponent(
                                  `{gvc="${workloadGvc}", location="${deployment?.name}", workload="${
                                    workload.name
                                  }", replica="${`${workload.name}-${replicaIndex}`}", container="${container.name}"}`,
                                )}&execute=true&live=false`;
                                navigate(logLink);
                                break;
                              case "tail":
                                const tailLink = `${basePath}/logs?query=${encodeURIComponent(
                                  `{gvc="${workloadGvc}", location="${deployment?.name}", workload="${
                                    workload.name
                                  }", replica="${`${workload.name}-${replicaIndex}`}", container="${container.name}"}`,
                                )}&execute=true&live=true`;
                                navigate(tailLink);
                                break;
                              case "connect":
                                const connectLink = `${basePath}/connect?location=${
                                  deployment?.name
                                }&replica=${`${workload.name}-${replicaIndex}`}&container=${
                                  container.name
                                }&autoConnect=true`;
                                navigate(connectLink);
                                break;
                              default:
                                break;
                            }
                          },
                        }}
                      >
                        <button type={"button"} className={`ml-2 color-link`} style={{ width: 24 }}>
                          <MoreVertical style={{ minWidth: 18, minHeight: 18, width: 18, height: 18 }} />
                        </button>
                      </Dropdown>
                    </div>
                  </div>
                </div>
                <Message
                  messages={[container?.message || ""]}
                  isHealthy={container.ready}
                  logsLink={pathname.split("/deployments")[0] + "/logs?execute=true"}
                  eventsLink={pathname.split("/deployments")[0] + "/events"}
                  className={`pl-4 mb-2`}
                />
                {isVolumeSetsReady && viewVolumes && containerVolumes.length < 1 ? (
                  <div className="text-sm pl-4">No volumes attached to this container</div>
                ) : null}
                <div className={`${containerVolumes.length > 0 && viewVolumes ? "block" : "hidden"} py-2 mx-4`}>
                  <div className={`flex items-center px-4 py-2 font-semibold table-labels rounded text-sm`}>
                    <div className="w-5/12">Volume Set</div>
                    <div className="w-7/12 flex items-center">
                      <div className="flex-1 flex-grow">Lifecycle</div>
                      <div className="flex-1 flex-grow" />
                      <div className="flex-1 flex-grow" />
                      <div className="flex-1 flex-grow">Volume Size</div>
                    </div>
                  </div>
                  {containerVolumes.map((containerVolume, containerVolumeIndex) => {
                    const lastItem = containerVolumeIndex === containerVolumes.length - 1;

                    return (
                      <div
                        key={String(containerVolume.index) + containerVolumeIndex}
                        className={clsx(`flex items-center px-4`, { "border-b": !lastItem })}
                      >
                        <div className="w-5/12 truncate pr-2">
                          <Link className="color-link" to={`/console` + containerVolume.volumeSetLink}>
                            {containerVolume.volumeSetName}
                          </Link>
                        </div>
                        <div className="w-7/12 flex items-center">
                          <div
                            className={`gap-2 flex-1 flex-grow ${
                              containerVolume.lifecycle === "bound" ? "color-action" : ""
                            }`}
                          >
                            {containerVolume.lifecycle?.slice(0, 1).toUpperCase()}
                            {containerVolume.lifecycle?.slice(1)}
                          </div>
                          <div className="flex-1 flex-grow"></div>
                          <div className="flex-1 flex-grow"></div>
                          <div className="flex-1 flex-grow">{containerVolume.currentSize}GB</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
